// File Name: Testimonials.jsx

import React, {
  useEffect,
  useState,
} from "react";

import {
  Star,
  Quote,
  ArrowLeft,
  ArrowRight,
} from "lucide-react";

import {
  motion,
  AnimatePresence,
} from "framer-motion";

/* PROFESSIONAL STATIC TESTIMONIALS */

const TESTIMONIALS = [
  {
    name: "Michael Anderson",
    role: "Operations Director",
    company: "NovaTech Solutions",
    rating: 5,
    text:
      "Exceptional service and highly professional execution. Their team delivered our project ahead of schedule with excellent communication and technical precision.",
  },

  {
    name: "Daniel Carter",
    role: "Founder & CEO",
    company: "BrightEdge Digital",
    rating: 5,
    text:
      "One of the most reliable IT partners we’ve worked with. Their innovative solutions and commitment to quality helped us scale our operations efficiently.",
  },

  {
    name: "Sophia Williams",
    role: "HR Director",
    company: "VisionCore Inc.",
    rating: 5,
    text:
      "Their staffing and HR solutions streamlined our recruitment process significantly. We experienced faster hiring and improved candidate quality.",
  },

  {
    name: "James Walker",
    role: "Business Consultant",
    company: "Inspire Global Group",
    rating: 5,
    text:
      "The digital transformation strategies they implemented improved our business workflow and reduced operational costs substantially.",
  },

  {
    name: "Emily Johnson",
    role: "Project Manager",
    company: "FutureTech Systems",
    rating: 5,
    text:
      "Very professional team with strong technical expertise. They understood our requirements perfectly and exceeded expectations in every aspect.",
  },

  {
    name: "Amina Rahman",
    role: "Marketing Head",
    company: "EliteSphere Media",
    rating: 5,
    text:
      "Outstanding creativity and technical support throughout the project. Their dedication and responsiveness made the entire experience seamless.",
  },

  {
    name: "Omar Khalid",
    role: "Managing Director",
    company: "PrimeAxis Technologies",
    rating: 5,
    text:
      "Their team provided secure and scalable enterprise solutions tailored perfectly to our business needs. Highly recommended for professional IT services.",
  },

  {
    name: "Jessica Brown",
    role: "Operations Manager",
    company: "Vertex Innovations",
    rating: 5,
    text:
      "From planning to deployment, the entire process was smooth and transparent. Their professionalism and attention to detail were impressive.",
  },

  {
    name: "Noah Mitchell",
    role: "CTO",
    company: "CloudBridge Labs",
    rating: 5,
    text:
      "Excellent development standards and modern solutions. The final product was secure, scalable, and visually outstanding.",
  },
];

export default function Testimonials() {

  const [index, setIndex] =
    useState(0);

  const [visibleCount, setVisibleCount] =
    useState(3);

  /* RESPONSIVE CARD COUNT */

  useEffect(() => {

    const handleResize = () => {

      if (window.innerWidth < 768) {

        setVisibleCount(1);

      } else if (window.innerWidth < 1024) {

        setVisibleCount(2);

      } else {

        setVisibleCount(3);

      }

    };

    handleResize();

    window.addEventListener(
      "resize",
      handleResize
    );

    return () =>
      window.removeEventListener(
        "resize",
        handleResize
      );

  }, []);

  /* AUTO SLIDER */

  useEffect(() => {

    const timer =
      setInterval(() => {

        setIndex((prev) =>
          (prev + 1) %
          TESTIMONIALS.length
        );

      }, 5000);

    return () =>
      clearInterval(timer);

  }, []);

  const nextSlide = () => {

    setIndex((prev) =>
      (prev + 1) %
      TESTIMONIALS.length
    );

  };

  const prevSlide = () => {

    setIndex((prev) =>
      prev === 0
        ? TESTIMONIALS.length - 1
        : prev - 1
    );

  };

  const getVisibleTestimonials = () => {

    const result = [];

    for (
      let i = 0;
      i < visibleCount;
      i++
    ) {

      result.push(
        TESTIMONIALS[
          (index + i) %
            TESTIMONIALS.length
        ]
      );

    }

    return result;

  };

  const visibleTestimonials =
    getVisibleTestimonials();

  return (
    <>
      {/* PREMIUM ANIMATIONS */}

      <style>
        {`

          @keyframes floatCard {

            0% {
              transform:
                translateY(0px);
            }

            50% {
              transform:
                translateY(-8px);
            }

            100% {
              transform:
                translateY(0px);
            }
          }

          .testimonial-card:hover {

            transform:
              translateY(-10px);

            transition:
              all 0.4s ease;
          }

          @media (max-width: 768px) {

            .testimonial-card {

              animation:
                floatCard 5s ease-in-out infinite;
            }
          }

        `}
      </style>

      <section
        className="
          relative

          py-24
          md:py-28

          overflow-hidden

          bg-gradient-to-b
          from-white
          via-purple-50/40
          to-white
        "
      >

        {/* BACKGROUND GLOWS */}

        <div
          className="
            absolute
            top-[-120px]
            left-[-120px]

            w-[420px]
            h-[420px]

            bg-purple-300/20

            rounded-full

            blur-[120px]
          "
        />

        <div
          className="
            absolute
            bottom-[-120px]
            right-[-120px]

            w-[420px]
            h-[420px]

            bg-fuchsia-300/20

            rounded-full

            blur-[120px]
          "
        />

        {/* GRID BG */}

        <div
          className="
            absolute
            inset-0

            opacity-[0.03]

            [background-image:linear-gradient(to_right,#7c3aed_1px,transparent_1px),linear-gradient(to_bottom,#7c3aed_1px,transparent_1px)]

            [background-size:60px_60px]
          "
        />

        <div
          className="
            relative

            max-w-7xl
            mx-auto

            px-5
            sm:px-6
            md:px-8
          "
        >

          {/* HEADER */}

          <div
            className="
              text-center

              max-w-3xl

              mx-auto

              mb-16
            "
          >

            <div
              className="
                text-purple-600

                uppercase

                tracking-[0.35em]

                text-xs
                sm:text-sm

                font-bold

                mb-4
              "
            >

              TESTIMONIALS

            </div>

            <h2
              className="
                text-4xl
                sm:text-5xl
                md:text-6xl

                font-bold

                text-slate-900

                leading-tight

                mb-6
              "
              style={{
                fontFamily: "Sora",
              }}
            >

              What They’re Saying

            </h2>

            <p
              className="
                text-slate-600

                text-base
                md:text-lg

                leading-relaxed
              "
            >

              Trusted by businesses worldwide
              for delivering scalable,
              secure and high-performance
              digital solutions.

            </p>

          </div>

          {/* TESTIMONIAL CARDS */}

          <div
            className={`
              grid

              gap-6
              md:gap-8

              ${
                visibleCount === 1
                  ? "grid-cols-1"
                  : visibleCount === 2
                  ? "grid-cols-2"
                  : "grid-cols-3"
              }
            `}
          >

            <AnimatePresence mode="wait">

              {visibleTestimonials.map(
                (testimonial, i) => (

                  <motion.div
                    key={`${testimonial.name}-${index}-${i}`}

                    initial={{
                      opacity: 0,
                      y: 30,
                    }}

                    animate={{
                      opacity: 1,
                      y: 0,
                    }}

                    exit={{
                      opacity: 0,
                      y: -20,
                    }}

                    transition={{
                      duration: 0.5,
                    }}

                    className="
                      testimonial-card

                      relative

                      overflow-hidden

                      rounded-[30px]

                      border
                      border-purple-100/60

                      bg-white/80

                      backdrop-blur-xl

                      p-8

                      shadow-[0_15px_50px_rgba(120,70,180,0.08)]

                      hover:shadow-[0_25px_70px_rgba(120,70,180,0.15)]

                      transition-all
                      duration-500
                    "
                  >

                    {/* CARD GLOW */}

                    <div
                      className="
                        absolute

                        top-0
                        right-0

                        w-32
                        h-32

                        bg-purple-300/20

                        rounded-full

                        blur-3xl
                      "
                    />

                    {/* QUOTE ICON */}

                    <div
                      className="
                        w-14
                        h-14

                        rounded-full

                        bg-gradient-to-br
                        from-[#6D3B8C]
                        via-[#9B4D96]
                        to-[#C67BCF]

                        flex
                        items-center
                        justify-center

                        mb-6

                        shadow-lg
                        shadow-purple-500/20
                      "
                    >

                      <Quote
                        className="
                          w-6
                          h-6

                          text-white
                        "
                      />

                    </div>

                    {/* STARS */}

                    <div
                      className="
                        flex

                        gap-1.5

                        mb-5
                      "
                    >

                      {Array.from({
                        length:
                          testimonial.rating,
                      }).map((_, star) => (

                        <Star
                          key={star}

                          className="
                            w-4
                            h-4

                            fill-yellow-400

                            text-yellow-400
                          "
                        />

                      ))}

                    </div>

                    {/* TEXT */}

                    <p
                      className="
                        text-slate-600

                        leading-relaxed

                        text-[15px]
                        md:text-base

                        mb-8

                        min-h-[140px]
                      "
                    >

                      {testimonial.text}

                    </p>

                    {/* AUTHOR */}

                    <div
                      className="
                        flex

                        items-center

                        gap-4
                      "
                    >

                      {/* AVATAR */}

                      <div
                        className="
                          w-14
                          h-14

                          rounded-full

                          bg-gradient-to-br
                          from-purple-500
                          to-fuchsia-400

                          flex
                          items-center
                          justify-center

                          text-white

                          font-bold
                          text-lg

                          shadow-md
                        "
                      >

                        {testimonial.name
                          ?.charAt(0)}

                      </div>

                      <div>

                        <div
                          className="
                            font-semibold

                            text-slate-900

                            text-lg
                          "
                        >

                          {testimonial.name}

                        </div>

                        <div
                          className="
                            text-slate-500

                            text-sm
                          "
                        >

                          {testimonial.role}
                          {" • "}
                          {
                            testimonial.company
                          }

                        </div>

                      </div>

                    </div>

                  </motion.div>

                )
              )}

            </AnimatePresence>

          </div>

          {/* NAVIGATION */}

          <div
            className="
              flex

              items-center
              justify-center

              gap-4

              mt-14
            "
          >

            <button
              onClick={prevSlide}

              className="
                w-12
                h-12

                rounded-full

                bg-white

                border
                border-purple-100

                flex
                items-center
                justify-center

                text-slate-700

                shadow-sm

                hover:bg-purple-50

                hover:scale-105

                transition-all
                duration-300
              "
            >

              <ArrowLeft
                className="
                  w-5
                  h-5
                "
              />

            </button>

            {/* DOTS */}

            <div
              className="
                flex

                gap-3
              "
            >

              {TESTIMONIALS.map(
                (_, i) => (

                  <button
                    key={i}

                    onClick={() =>
                      setIndex(i)
                    }

                    className={`
                      h-2.5

                      rounded-full

                      transition-all
                      duration-300

                      ${
                        index === i
                          ? `
                            w-10

                            bg-gradient-to-r
                            from-purple-500
                            to-fuchsia-400
                          `
                          : `
                            w-2.5

                            bg-purple-200
                          `
                      }
                    `}
                  />

                )
              )}

            </div>

            <button
              onClick={nextSlide}

              className="
                w-12
                h-12

                rounded-full

                bg-white

                border
                border-purple-100

                flex
                items-center
                justify-center

                text-slate-700

                shadow-sm

                hover:bg-purple-50

                hover:scale-105

                transition-all
                duration-300
              "
            >

              <ArrowRight
                className="
                  w-5
                  h-5
                "
              />

            </button>

          </div>

        </div>

      </section>
    </>
  );
}